import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface ConfigProviderDto {
  ConfigID: number;
  ProviderID: string;
  DeployStatus: 'pending' | 'deployed' | 'error';
  DeployedAt: string | null;
  DeployError: string | null;
  DeployedBy: string | null;
  GcsUrl: string | null;
}

export interface JsClientConfigDto {
  ID: number;
  Name: string;
  PublisherID: string;
  Autostart: 0 | 1;
  TCF: 0 | 1;
  Publisher: 0 | 1;
  Scripts: string[];
  Exclude: string[];
  Fonts: { version: string } | null;
  Modules: string[];
  CallbackScript: string | null;
  PrepareScript: string | null;
  OverwriteScript: string | null;
  SnippetAppend: string | null;
  Status: 0 | 1;
  Template: 'tnc' | 'retailor';
  CreatedAt: string;
  UpdatedAt: string;
  Providers: ConfigProviderDto[];
}

export interface DeployResultDto {
  deployed: string[];
  errors: { providerId: string; error: string }[];
}

export interface PurgeResultDto {
  invalidated: string[];
  errors: { path: string; error: string }[];
  skipped: boolean;
}

export interface CreateJsClientConfigBody {
  Name: string;
  PublisherID: string;
  Autostart?: boolean;
  TCF?: boolean;
  Publisher?: boolean;
  Scripts: string[];
  Exclude?: string[];
  Fonts?: { version: string } | null;
  Modules?: string[];
  CallbackScript?: string | null;
  PrepareScript?: string | null;
  OverwriteScript?: string | null;
  SnippetAppend?: string | null;
  Status?: number;
  ProviderIDs?: string[];
  Template?: 'tnc' | 'retailor';
}

export interface UpdateJsClientConfigBody extends Partial<CreateJsClientConfigBody> {
  ID: number;
}

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class JsClientApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getConfigs(): Observable<JsClientConfigDto[]> {
    const endpoint = this.composeEndpoint('/webapp/js-client/configs');
    return this.http.get<GenericResponse<JsClientConfigDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[js-client-api] list failed', error);
        return throwError(() => error);
      })
    );
  }

  getConfig(id: number): Observable<JsClientConfigDto> {
    const endpoint = this.composeEndpoint(`/webapp/js-client/configs/${id}`);
    return this.http.get<GenericResponse<JsClientConfigDto>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload!),
      catchError(error => {
        console.error('[js-client-api] get failed', error);
        return throwError(() => error);
      })
    );
  }

  createConfig(body: CreateJsClientConfigBody): Observable<JsClientConfigDto> {
    const endpoint = this.composeEndpoint('/webapp/js-client/configs');
    return this.http.post<GenericResponse<JsClientConfigDto>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload!),
      catchError(error => {
        console.error('[js-client-api] create failed', error);
        return throwError(() => error);
      })
    );
  }

  updateConfig(body: UpdateJsClientConfigBody): Observable<JsClientConfigDto> {
    const endpoint = this.composeEndpoint('/webapp/js-client/configs');
    return this.http.put<GenericResponse<JsClientConfigDto>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload!),
      catchError(error => {
        console.error('[js-client-api] update failed', error);
        return throwError(() => error);
      })
    );
  }

  deleteConfig(id: number): Observable<{ ID: number }> {
    const endpoint = this.composeEndpoint('/webapp/js-client/configs');
    return this.http.delete<GenericResponse<{ ID: number }>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        body: { ID: id }
      }
    ).pipe(
      map(res => res.payload ?? { ID: id }),
      catchError(error => {
        console.error('[js-client-api] delete failed', error);
        return throwError(() => error);
      })
    );
  }

  purge(configId: number, providerIds?: string[]): Observable<PurgeResultDto> {
    const endpoint = this.composeEndpoint('/webapp/js-client/purge');
    const body: { configId: number; providerIds?: string[] } = { configId };
    if (providerIds && providerIds.length > 0) {
      body.providerIds = providerIds;
    }
    return this.http.post<GenericResponse<PurgeResultDto>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? { invalidated: [], errors: [], skipped: false }),
      catchError(error => {
        console.error('[js-client-api] purge failed', error);
        return throwError(() => error);
      })
    );
  }

  deploy(configId: number, providerIds?: string[]): Observable<DeployResultDto> {
    const endpoint = this.composeEndpoint('/webapp/js-client/deploy');
    const body: { configId: number; providerIds?: string[] } = { configId };
    if (providerIds && providerIds.length > 0) {
      body.providerIds = providerIds;
    }
    return this.http.post<GenericResponse<DeployResultDto>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? { deployed: [], errors: [] }),
      catchError(error => {
        console.error('[js-client-api] deploy failed', error);
        return throwError(() => error);
      })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean): HttpHeaders {
    if (useRemote) {
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const token = this.authService.getTncApiToken();
      if (token) headers = headers.set('Authorization', token);
      return headers;
    }

    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }
}
